import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Avatar,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

// ─── Field Config ─────────────────────────────────────────────────────────────

const FIELD_LABELS = {
  image: "الصورة الرئيسية",
  name: "الاسم",
  name_ar: "الاسم (عربي)",
  name_en: "الاسم (إنجليزي)",
  sammary: "الملخص",
  description: "الوصف",
  description_ar: "الوصف (عربي)",
  description_en: "الوصف (إنجليزي)",
  price: "السعر",
  stock: "المخزون",
  discount: "الخصم %",
  size: "الحجم",
  unit: "الوحدة",
  category: "القسم الرئيسي",
  mid_category: "القسم الفرعي",
  sub_category: "القسم الفرعي الثانوي",
  max_quantity: "الحد الأقصى للكمية",
  min_quantity: "الحد الأدنى للكمية",
  discount_date: "تاريخ بداية الخصم",
  discount_date_to: "تاريخ نهاية الخصم",
};

const PRICE_FIELDS = new Set(["price"]);
const IMAGE_FIELDS = new Set(["image"]);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const normalizeVal = (v) => String(v ?? "").trim();

const displayVal = (v) =>
  v === null || v === undefined || v === "" ? "—" : String(v);

const compareVariations = (currentVars = [], newVars = []) => {
  const result = [];

  // Map current variations by id
  const currentMap = {};
  currentVars.forEach((v) => {
    currentMap[String(v.id)] = v;
  });

  newVars.forEach((newV) => {
    const id = String(newV.id);
    const currentV = currentMap[id];

    if (!currentV) {
      result.push({ type: "added", newV });
      return;
    }

    const changedKeys = [];
    const varFieldLabels = {
      type: "نوع السمة",
      price: "السعر",
      qty: "الكمية",
      percentage_discount: "خصم %",
      fixed_discount: "خصم ثابت",
    };

    Object.keys(varFieldLabels).forEach((key) => {
      if (normalizeVal(currentV[key]) !== normalizeVal(newV[key])) {
        changedKeys.push({
          key,
          label: varFieldLabels[key],
          oldVal: currentV[key],
          newVal: newV[key],
        });
      }
    });

    const hasNewImages = newV.new_images?.length > 0;

    if (changedKeys.length > 0 || hasNewImages) {
      result.push({ type: "changed", id, changedKeys, newV, currentV });
    }
  });

  return result;
};

// ─── Component ────────────────────────────────────────────────────────────────

const UpdateDetailsModal = ({
  open,
  onClose,
  changeRequest,
  currentProduct,
}) => {
  if (!changeRequest || !currentProduct) return null;

  const isNewProduct = !changeRequest.current_data;

  const currentProductData = changeRequest.current_data?.product || {};
  const newProductData = changeRequest.new_data?.product || {};
  const currentVariations = changeRequest.current_data?.variations || [];
  const newVariations = changeRequest.new_data?.variations || [];
  const newImages = changeRequest.new_data?.gallery?.new_images || [];

  // Collect changed (or new) product fields
  const changedFields = Object.entries(FIELD_LABELS)
    .filter(([key]) => {
      if (isNewProduct) {
        const v = newProductData[key];
        return v !== null && v !== undefined && v !== "";
      }
      return (
        normalizeVal(currentProductData[key]) !==
        normalizeVal(newProductData[key])
      );
    })
    .map(([key, label]) => {
      const oldVal = isNewProduct ? null : currentProductData[key];
      const newVal = newProductData[key];
      return { key, label, oldVal, newVal, displayOld: oldVal, displayNew: newVal };
    });

  const variationChanges = compareVariations(currentVariations, newVariations);

  const hasContent =
    changedFields.length > 0 ||
    variationChanges.length > 0 ||
    newImages.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "14px",
          direction: "rtl",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #F0F0F0",
          py: 2,
          px: 3,
          bgcolor: "#FAFAFA",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          {currentProduct.image && (
            <Avatar
              src={currentProduct.image}
              variant="rounded"
              sx={{ width: 44, height: 44, borderRadius: "8px" }}
            />
          )}
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontWeight={700} fontSize={15} lineHeight={1.2}>
                {currentProduct.name_ar || currentProduct.name}
              </Typography>
              <Chip
                size="small"
                icon={
                  isNewProduct ? (
                    <AddCircleOutlineIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <EditOutlinedIcon sx={{ fontSize: 14 }} />
                  )
                }
                label={isNewProduct ? "منتج جديد" : "طلب تعديل"}
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 600,
                  bgcolor: isNewProduct
                    ? "rgba(37,99,235,0.08)"
                    : "rgba(161,92,0,0.08)",
                  color: isNewProduct ? "#2563EB" : "#A15C00",
                  "& .MuiChip-icon": {
                    color: "inherit",
                  },
                }}
              />
            </Box>
            <Typography fontSize={12} color="text.secondary" mt={0.3}>
              طلب #{changeRequest.id}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {!hasContent ? (
          <Box py={5} textAlign="center">
            <Typography color="text.secondary" fontSize={14}>
              لا توجد تعديلات على البيانات
            </Typography>
          </Box>
        ) : (
          <>
            {/* ── Product Field Changes ── */}
            {changedFields.length > 0 && (
              <>
                {/* Section label */}
                <Box px={3} pt={2} pb={1}>
                  <Typography
                    fontSize={12}
                    fontWeight={700}
                    color="text.secondary"
                  >
                    {isNewProduct
                      ? "بيانات المنتج"
                      : "التعديلات على بيانات المنتج"}
                  </Typography>
                </Box>

                {/* Column Headers */}
                <Box
                  display="grid"
                  gridTemplateColumns={
                    isNewProduct ? "180px 1fr" : "180px 1fr 1fr"
                  }
                  sx={{ bgcolor: "#F7F7F7", borderBottom: "1px solid #EBEBEB" }}
                >
                  <Box px={2} py={1.2} />
                  {!isNewProduct && (
                    <Box
                      px={2}
                      py={1.2}
                      sx={{
                        borderRight: "1px solid #EBEBEB",
                        borderLeft: "1px solid #EBEBEB",
                      }}
                    >
                      <Typography
                        fontSize={12}
                        fontWeight={700}
                        color="#DC2626"
                      >
                        قبل التعديل
                      </Typography>
                    </Box>
                  )}
                  <Box px={2} py={1.2}>
                    <Typography
                      fontSize={12}
                      fontWeight={700}
                      color={isNewProduct ? "#2563EB" : "#059669"}
                    >
                      {isNewProduct ? "القيمة" : "بعد التعديل"}
                    </Typography>
                  </Box>
                </Box>

                {/* Rows */}
                {changedFields.map(({ key, label, oldVal, newVal, displayOld, displayNew }, i) => {
                  const priceSuffix = PRICE_FIELDS.has(key) ? " ج.م" : "";
                  const isImage = IMAGE_FIELDS.has(key);
                  return (
                    <Box
                      key={key}
                      display="grid"
                      gridTemplateColumns={
                        isNewProduct ? "180px 1fr" : "180px 1fr 1fr"
                      }
                      sx={{
                        bgcolor: i % 2 === 0 ? "white" : "#FCFCFC",
                        borderBottom: "1px solid #F3F4F6",
                        "&:last-of-type": { borderBottom: "none" },
                      }}
                    >
                      {/* Field name */}
                      <Box
                        px={2}
                        py={1.5}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ borderLeft: "1px solid #EBEBEB" }}
                      >
                        <Typography fontSize={13} fontWeight={600} color="#444">
                          {label}
                        </Typography>
                        {!isNewProduct && (
                          <Chip
                            label="تم تغييره"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: 10,
                              fontWeight: 600,
                              bgcolor: "rgba(161,92,0,0.08)",
                              color: "#A15C00",
                              "& .MuiChip-label": { px: 0.8 },
                            }}
                          />
                        )}
                      </Box>

                      {/* Before (only for updates) */}
                      {!isNewProduct && (
                        <Box
                          px={2}
                          py={1.5}
                          display="flex"
                          alignItems="center"
                          sx={{
                            borderLeft: "1px solid #EBEBEB",
                            bgcolor: "rgba(220,38,38,0.03)",
                          }}
                        >
                          {isImage ? (
                            <Box
                              component="img"
                              src={oldVal}
                              alt="before"
                              sx={{
                                width: 56,
                                height: 56,
                                borderRadius: "8px",
                                objectFit: "cover",
                                border: "2px solid #DC2626",
                                opacity: 0.85,
                              }}
                              onError={(e) => { e.target.style.display = "none"; }}
                            />
                          ) : (
                            <Typography
                              fontSize={13}
                              color="#DC2626"
                              sx={{ opacity: 0.85 }}
                            >
                              {displayVal(displayOld)}
                              {priceSuffix}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* After / Value */}
                      <Box
                        px={2}
                        py={1.5}
                        display="flex"
                        alignItems="center"
                        sx={{
                          bgcolor: isNewProduct
                            ? "rgba(37,99,235,0.03)"
                            : "rgba(5,150,105,0.03)",
                        }}
                      >
                        {isImage ? (
                          <Box
                            component="img"
                            src={newVal}
                            alt="after"
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: "8px",
                              objectFit: "cover",
                              border: `2px solid ${isNewProduct ? "#2563EB" : "#059669"}`,
                            }}
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        ) : (
                          <Typography
                            fontSize={13}
                            color={isNewProduct ? "#2563EB" : "#059669"}
                            fontWeight={500}
                          >
                            {displayVal(displayNew)}
                            {priceSuffix}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}

            {/* ── Variation Changes ── */}
            {variationChanges.length > 0 && (
              <Box px={3} py={2} sx={{ borderTop: "1px solid #F0F0F0" }}>
                <Typography
                  fontSize={12}
                  fontWeight={700}
                  color="text.secondary"
                  mb={1.5}
                >
                  التعديلات على السمات
                </Typography>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {variationChanges.map((item, i) => (
                    <Box
                      key={i}
                      sx={{
                        border: "1px solid",
                        borderColor:
                          item.type === "added" ? "#93C5FD" : "#EBEBEB",
                        borderRadius: "10px",
                        overflow: "hidden",
                      }}
                    >
                      {/* Variation header */}
                      <Box
                        px={2}
                        py={1}
                        sx={{
                          bgcolor:
                            item.type === "added"
                              ? "rgba(37,99,235,0.05)"
                              : "#F9F9F9",
                          borderBottom: "1px solid",
                          borderColor:
                            item.type === "added" ? "#93C5FD" : "#EBEBEB",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {item.type === "added" ? (
                          <AddCircleOutlineIcon
                            sx={{ fontSize: 14, color: "#2563EB" }}
                          />
                        ) : (
                          <EditOutlinedIcon
                            sx={{ fontSize: 14, color: "#A15C00" }}
                          />
                        )}
                        <Typography
                          fontSize={12}
                          fontWeight={700}
                          color={item.type === "added" ? "#2563EB" : "#A15C00"}
                        >
                          {item.type === "added"
                            ? `سمة جديد — ${item.newV.type}`
                            : `تعديل سمة #${item.id} — ${item.newV.type}`}
                        </Typography>
                      </Box>

                      {/* Changed fields within variation */}
                      {item.type === "changed" && (
                        <Box
                          display="grid"
                          gridTemplateColumns="140px 1fr 1fr"
                          sx={{
                            bgcolor: "#F7F7F7",
                            borderBottom: "1px solid #EBEBEB",
                          }}
                        >
                          <Box px={2} py={1} />
                          <Box
                            px={2}
                            py={1}
                            sx={{
                              borderRight: "1px solid #EBEBEB",
                              borderLeft: "1px solid #EBEBEB",
                            }}
                          >
                            <Typography
                              fontSize={11}
                              fontWeight={700}
                              color="#DC2626"
                            >
                              قبل
                            </Typography>
                          </Box>
                          <Box px={2} py={1}>
                            <Typography
                              fontSize={11}
                              fontWeight={700}
                              color="#059669"
                            >
                              بعد
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {item.type === "changed" &&
                        item.changedKeys.map(
                          ({ key, label, oldVal, newVal }, j) => (
                            <Box
                              key={key}
                              display="grid"
                              gridTemplateColumns="140px 1fr 1fr"
                              sx={{
                                bgcolor: j % 2 === 0 ? "white" : "#FCFCFC",
                                borderBottom: "1px solid #F3F4F6",
                                "&:last-of-type": { borderBottom: "none" },
                              }}
                            >
                              <Box
                                px={2}
                                py={1.2}
                                display="flex"
                                alignItems="center"
                                sx={{ borderLeft: "1px solid #EBEBEB" }}
                              >
                                <Typography
                                  fontSize={12}
                                  fontWeight={600}
                                  color="#444"
                                >
                                  {label}
                                </Typography>
                              </Box>
                              <Box
                                px={2}
                                py={1.2}
                                display="flex"
                                alignItems="center"
                                sx={{
                                  borderLeft: "1px solid #EBEBEB",
                                  bgcolor: "rgba(220,38,38,0.03)",
                                }}
                              >
                                <Typography
                                  fontSize={12}
                                  color="#DC2626"
                                  sx={{
                                    textDecoration: "line-through",
                                    opacity: 0.8,
                                  }}
                                >
                                  {displayVal(oldVal)}
                                  {key === "price" ? " ج.م" : ""}
                                </Typography>
                              </Box>
                              <Box
                                px={2}
                                py={1.2}
                                display="flex"
                                alignItems="center"
                                sx={{ bgcolor: "rgba(5,150,105,0.03)" }}
                              >
                                <Typography
                                  fontSize={12}
                                  color="#059669"
                                  fontWeight={500}
                                >
                                  {displayVal(newVal)}
                                  {key === "price" ? " ج.م" : ""}
                                </Typography>
                              </Box>
                            </Box>
                          ),
                        )}

                      {item.type === "changed" &&
                        item.newV.new_images?.length > 0 && (
                          <Box
                            px={2}
                            py={1.5}
                            sx={{ borderTop: "1px solid #F3F4F6" }}
                          >
                            <Typography
                              fontSize={11}
                              fontWeight={700}
                              color="text.secondary"
                              mb={1}
                            >
                              صور جديدة ({item.newV.new_images.length})
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={1}>
                              {item.newV.new_images.map((src, idx) => (
                                <Box
                                  key={idx}
                                  component="img"
                                  src={src}
                                  alt={`var-img-${idx}`}
                                  sx={{
                                    width: 64,
                                    height: 64,
                                    borderRadius: "8px",
                                    objectFit: "cover",
                                    border: "1px solid #E0E0E0",
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}

                      {item.type === "added" && (
                        <Box
                          display="flex"
                          gap={3}
                          px={2}
                          py={1.5}
                          flexWrap="wrap"
                        >
                          {[
                            { label: "النوع", val: item.newV.type },
                            { label: "السعر", val: `${item.newV.price} ج.م` },
                            { label: "الكمية", val: item.newV.qty },
                            Number(item.newV.percentage_discount) > 0 && {
                              label: "خصم %",
                              val: `${item.newV.percentage_discount}%`,
                            },
                          ]
                            .filter(Boolean)
                            .map(({ label, val }) => (
                              <Box key={label}>
                                <Typography
                                  fontSize={11}
                                  color="text.secondary"
                                >
                                  {label}
                                </Typography>
                                <Typography
                                  fontSize={13}
                                  fontWeight={500}
                                  color="#2563EB"
                                >
                                  {val}
                                </Typography>
                              </Box>
                            ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* ── New Images ── */}
            {newImages.length > 0 && (
              <Box px={3} py={2} sx={{ borderTop: "1px solid #F0F0F0" }}>
                <Typography
                  fontSize={12}
                  fontWeight={700}
                  color="text.secondary"
                  mb={1}
                >
                  صور جديدة ({newImages.length})
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {newImages.map((src, i) => (
                    <Box
                      key={i}
                      component="img"
                      src={src}
                      alt={`new-img-${i}`}
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: "8px",
                        objectFit: "cover",
                        border: "1px solid #E0E0E0",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ))}
                </Box>
                <Typography fontSize={11} color="text.secondary" mt={0.5}>
                  ستُطبَّق الصور عند الموافقة
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UpdateDetailsModal;
